import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { setConfirmed, setUnsavedChanges } from '../_redux/UnsavedSlice';
import { useDispatch } from 'react-redux';

/**
 * Custom hook to warn users about unsaved changes when attempting to navigate away from the current page.
 * This hook handles both browser/tab navigation (like refresh or close) and in-app route changes.
 *
 * @param {boolean} unsavedChanges - A boolean indicating if there are unsaved changes.
 * @param {string} message - A custom warning message to show when navigating away with unsaved changes.
 */
const useUnsavedChangesWarning = (
  unsavedChanges,
  message = 'You have unsaved changes. Are you sure you want to leave?'
) => {
  const location = useLocation();
  const currentPath = location.pathname;

  const previousPathRef = useRef(currentPath);
  const unsavedRef = useRef(unsavedChanges);
  const isHookActiveRef = useRef(true);
  const dispatch = useDispatch();

  useEffect(() => {
    unsavedRef.current = unsavedChanges;
    dispatch(setUnsavedChanges(unsavedChanges));
  }, [unsavedChanges]);

  useEffect(() => {
    isHookActiveRef.current = true;

    const handleBeforeUnload = (event) => {
      // if (isHookActiveRef.current && unsavedRef.current) {
      //   event.preventDefault();
      // event.returnValue = message;
      // }
    };

    const handlePopState = () => {
      const nextPath = window.location.pathname;
      if (
        isHookActiveRef.current &&
        unsavedRef.current &&
        nextPath !== currentPath
      ) {
        const confirmLeave = window.confirm(message);
        if (!confirmLeave) {
          window.history.pushState(null, '', previousPathRef.current);
        } else {
          previousPathRef.current = nextPath;
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      const nextPath =
        typeof args[2] === 'string' ? args[2] : window.location.pathname;
      if (
        isHookActiveRef.current &&
        unsavedRef.current &&
        nextPath !== currentPath
      ) {
        const confirmLeave = window.confirm(message);
        if (confirmLeave) {
          if (nextPath === '/ats/login') {
            const themeSettings = localStorage.getItem('themeSettings');
            previousPathRef.current = nextPath;
            Cookies.remove('userName');
            Cookies.remove('userPassword');
            localStorage.clear();
            sessionStorage.clear();
            localStorage.setItem('themeSettings', themeSettings);
          }
          if (nextPath === '/ats/layout') {
            dispatch(setConfirmed('toggle'));
            return;
          }
          if (nextPath === '/ats/layout1') {
            dispatch(setConfirmed('dropdown'));
            return;
          }
          if (nextPath === '/ats/layout2') {
            dispatch(setConfirmed('cancel'));
            return;
          }
          return originalPushState.apply(this, args);
        }
        return;
      }
      return originalPushState.apply(this, args);
    };

    window.history.replaceState = function (...args) {
      const nextPath =
        typeof args[2] === 'string' ? args[2] : window.location.pathname;
      if (
        isHookActiveRef.current &&
        unsavedRef.current &&
        nextPath !== currentPath
      ) {
        const confirmLeave = window.confirm(message);
        if (confirmLeave) {
          previousPathRef.current = nextPath;
          return originalReplaceState.apply(this, args);
        }
        return;
      }
      return originalReplaceState.apply(this, args);
    };

    return () => {
      isHookActiveRef.current = false;
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [message, currentPath]);
};

export default useUnsavedChangesWarning;
